import {
  MAX_FM_DEPTH,
  clonePreset,
  normalizePreset,
  type FilterType,
  type InstrumentSlot,
  type ModRoute,
  type ModSource,
  type ModTarget,
  type SynthPreset,
  type Waveform,
} from "../../../../../src/lib/audio";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";
import { ParamKnob } from "../ParamKnob";
import { Checkbox } from "../ui/Checkbox";
import { Slider } from "../ui/Slider";
import { instrumentPresetLearnPath } from "../../controls/instrumentPresetLeaves";
import { InstrumentBankBar } from "./InstrumentBankBar";

const WAVES: Waveform[] = ["sine", "tri", "saw", "square", "noise"];
const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const LFO_WAVES: Array<"sine" | "tri"> = ["sine", "tri"];
const MOD_SOURCES: ModSource[] = ["ampEnv", "modEnv", "lfo", "key"];
const MOD_TARGETS: ModTarget[] = ["pitch", "cutoff", "amp", "pwm", "fmDepth", "amDepth"];
const FM_TARGETS = ["osc1", "osc2", "both"] as const;

/**
 * `kind: "synth"` instrument body — store-driven form + bank bar + keyboard host.
 */
export function SynthEditor({ slot }: { slot: InstrumentSlot }) {
  const presets = useStoreSelector(projectStore, s => s.presets);
  const raw = presets[slot];
  const preset = raw ? normalizePreset(raw) : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const [octaveShift, setOctaveShift] = useState(0);

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setOctaveShift(0);
  }, [slot]);

  if (!preset) {
    return <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden">No preset for this slot.</div>;
  }

  function commit(next: SynthPreset): void {
    getLabActions()?.updateInstrumentPreset?.(slot, next);
  }

  function patchPath(path: string, value: unknown): void {
    const next = clonePreset(preset!);
    setPath(next as unknown as Record<string, unknown>, path, value);
    if (path === "glideMs" && typeof value === "number" && value <= 0) {
      delete next.glideMs;
    }
    commit(next);
  }

  function updateRoutes(routes: ModRoute[]): void {
    commit({ ...clonePreset(preset!), routes });
  }

  return (
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none" tabIndex={0}>
      <InstrumentBankBar slot={slot} />

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="px-[0.6rem] py-2">
          <Section title="Voice">
            <TextRow
              label="Name"
              value={preset.name}
              onChange={v => patchPath("name", v)}
            />
            <SliderRow
              label="Glide ms"
              path="glideMs"
              slot={slot}
              min={0}
              max={500}
              step={1}
              value={preset.glideMs ?? 0}
              onChange={v => patchPath("glideMs", v)}
            />
          </Section>

          <OscSection
            title="Osc 1"
            prefix="osc1"
            slot={slot}
            wave={preset.osc1.wave}
            octave={preset.osc1.octave}
            semis={preset.osc1.semis}
            detune={preset.osc1.detuneCents}
            level={preset.osc1.level}
            pwm={preset.osc1.pwm ?? 0.5}
            onPatch={patchPath}
          />
          <OscSection
            title="Osc 2"
            prefix="osc2"
            slot={slot}
            wave={preset.osc2.wave}
            octave={preset.osc2.octave}
            semis={preset.osc2.semis}
            detune={preset.osc2.detuneCents}
            level={preset.osc2.level}
            pwm={preset.osc2.pwm ?? 0.5}
            onPatch={patchPath}
          />
          <OscSection
            title="Osc 3 / modulator"
            prefix="osc3"
            slot={slot}
            wave={preset.osc3.wave}
            octave={preset.osc3.octave}
            semis={preset.osc3.semis}
            detune={preset.osc3.detuneCents}
            level={preset.osc3.level}
            pwm={preset.osc3.pwm ?? 0.5}
            onPatch={patchPath}
          />

          <Section title="FM / ring (osc 3 →)">
            <SelectRow
              label="Target"
              value={preset.fm.target}
              options={FM_TARGETS}
              onChange={v => patchPath("fm.target", v)}
            />
            <SliderRow
              label="FM depth"
              path="fm.fmDepth"
              slot={slot}
              min={0}
              max={MAX_FM_DEPTH}
              step={0.01}
              value={preset.fm.fmDepth}
              onChange={v => patchPath("fm.fmDepth", v)}
            />
            <SliderRow
              label="Ring"
              path="fm.amDepth"
              slot={slot}
              min={0}
              max={1}
              step={0.01}
              value={preset.fm.amDepth}
              onChange={v => patchPath("fm.amDepth", v)}
            />
            <SliderRow
              label="Vel → FM"
              path="fm.velocityToFm"
              slot={slot}
              min={0}
              max={1}
              step={0.01}
              value={preset.fm.velocityToFm}
              onChange={v => patchPath("fm.velocityToFm", v)}
            />
          </Section>

          <Section title="Noise">
            <SliderRow
              label="Level"
              path="noise.level"
              slot={slot}
              min={0}
              max={1}
              step={0.01}
              value={preset.noise.level}
              onChange={v => patchPath("noise.level", v)}
            />
          </Section>

          <Section title="Filter">
            <SelectRow
              label="Type"
              value={preset.filter.type}
              options={FILTERS}
              onChange={v => patchPath("filter.type", v)}
            />
            <SliderRow
              label="Cutoff Hz"
              path="filter.cutoffHz"
              slot={slot}
              min={40}
              max={12000}
              step={1}
              value={preset.filter.cutoffHz}
              onChange={v => patchPath("filter.cutoffHz", v)}
            />
            <SliderRow
              label="Res"
              path="filter.resonance"
              slot={slot}
              min={0}
              max={1}
              step={0.01}
              value={preset.filter.resonance}
              onChange={v => patchPath("filter.resonance", v)}
            />
          </Section>

          <Section title="Amp env">
            <SliderRow label="A" path="ampEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.a} onChange={v => patchPath("ampEnv.a", v)} />
            <SliderRow label="D" path="ampEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.d} onChange={v => patchPath("ampEnv.d", v)} />
            <SliderRow label="S" path="ampEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.ampEnv.s} onChange={v => patchPath("ampEnv.s", v)} />
            <SliderRow label="R" path="ampEnv.r" slot={slot} min={0.001} max={3} step={0.001} value={preset.ampEnv.r} onChange={v => patchPath("ampEnv.r", v)} />
          </Section>

          <Section title="Mod env">
            <SliderRow label="A" path="modEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.a} onChange={v => patchPath("modEnv.a", v)} />
            <SliderRow label="D" path="modEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.d} onChange={v => patchPath("modEnv.d", v)} />
            <SliderRow label="S" path="modEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.modEnv.s} onChange={v => patchPath("modEnv.s", v)} />
            <SliderRow label="R" path="modEnv.r" slot={slot} min={0.001} max={3} step={0.001} value={preset.modEnv.r} onChange={v => patchPath("modEnv.r", v)} />
          </Section>

          <Section title="LFO">
            <SelectRow
              label="Wave"
              value={preset.lfo.wave}
              options={LFO_WAVES}
              onChange={v => patchPath("lfo.wave", v)}
            />
            <SliderRow label="Rate Hz" path="lfo.rateHz" slot={slot} min={0} max={20} step={0.1} value={preset.lfo.rateHz} onChange={v => patchPath("lfo.rateHz", v)} />
            <SliderRow label="Depth" path="lfo.depth" slot={slot} min={0} max={1} step={0.01} value={preset.lfo.depth} onChange={v => patchPath("lfo.depth", v)} />
          </Section>
        </div>

        <ModRoutes
          slot={slot}
          routes={preset.routes}
          onChange={updateRoutes}
        />

        <div className="synth-slot-footer flex shrink-0 flex-wrap gap-[0.3rem] border-t border-white/[0.06] bg-lab-panel-bar px-[0.6rem] py-1.5">
          <button
            type="button"
            className="px-[0.45rem] py-1 text-[11px]"
            onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
          >
            Reset slot factory
          </button>
        </div>
      </div>

      <div className="synth-keys shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-[0.6rem] pt-1.5 pb-0.5">
        <div className="mb-[0.3rem] flex justify-between text-[10px] tracking-[0.05em] text-lab-muted uppercase">
          <span>Keyboard</span>
          <span className="muted">{formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={48}
          octaves={2}
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
      <p className="synth-keys-hint muted m-0 shrink-0 bg-lab-panel-bar px-[0.6rem] pb-2 text-[10px]">
          A–' white · W E T Y U O P black · Z/X octave · click keys to play
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-[0.55rem]">
      <h3 className="mb-[0.3rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-0.5">{children}</div>
    </section>
  );
}

function OscSection({
  title,
  prefix,
  slot,
  wave,
  octave,
  semis,
  detune,
  level,
  pwm,
  onPatch,
}: {
  title: string;
  prefix: string;
  slot: InstrumentSlot;
  wave: Waveform;
  octave: number;
  semis: number;
  detune: number;
  level: number;
  pwm: number;
  onPatch: (path: string, value: unknown) => void;
}) {
  return (
    <Section title={title}>
      <SelectRow label="Wave" value={wave} options={WAVES} onChange={v => onPatch(`${prefix}.wave`, v)} />
      <SliderRow label="Octave" path={`${prefix}.octave`} slot={slot} min={-3} max={3} step={1} value={octave} onChange={v => onPatch(`${prefix}.octave`, v)} />
      <SliderRow label="Semis" path={`${prefix}.semis`} slot={slot} min={-12} max={12} step={1} value={semis} onChange={v => onPatch(`${prefix}.semis`, v)} />
      <SliderRow label="Detune ¢" path={`${prefix}.detuneCents`} slot={slot} min={-100} max={100} step={1} value={detune} onChange={v => onPatch(`${prefix}.detuneCents`, v)} />
      <SliderRow label="Level" path={`${prefix}.level`} slot={slot} min={0} max={1} step={0.01} value={level} onChange={v => onPatch(`${prefix}.level`, v)} />
      <SliderRow label="PWM" path={`${prefix}.pwm`} slot={slot} min={0} max={1} step={0.01} value={pwm} onChange={v => onPatch(`${prefix}.pwm`, v)} />
    </Section>
  );
}

function TextRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={synthRowClass}>
      <span>{label}</span>
      <input
        type="text"
        className={synthControlClass}
        value={value}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className={synthRowClass}>
      <span>{label}</span>
      <select className={synthControlClass} value={value} onChange={event => onChange(event.target.value)}>
        {options.map(opt => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function SliderRow({
  label,
  path,
  slot,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  path: string;
  slot: InstrumentSlot;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className={synthRowClass}>
      <span>{label}</span>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        data-path={path}
        aria-label={label}
        learnPath={instrumentPresetLearnPath(slot, path)}
        onChange={onChange}
      />
      <span className={synthValClass}>{formatVal(path, value)}</span>
    </label>
  );
}

function ModRoutes({
  slot,
  routes,
  onChange,
}: {
  slot: InstrumentSlot;
  routes: ModRoute[];
  onChange: (routes: ModRoute[]) => void;
}) {
  return (
    <div className="border-t border-white/[0.06] px-[0.6rem] pb-[0.35rem]">
      <div className="my-[0.35rem] mb-1 flex items-center justify-between">
        <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
          Mod routes
        </h3>
        <button
          type="button"
          className="px-1.5 py-[0.18rem] text-[11px]"
          onClick={() =>
            onChange([
              ...routes,
              { source: "modEnv", target: "cutoff", depth: 1 },
            ])
          }
        >
          + Route
        </button>
      </div>
      <div className="flex flex-col gap-0.5">
        {routes.map((route, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_1fr_auto_auto_auto] items-center gap-1"
          >
            <select
              className={synthControlClass}
              value={route.source}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, source: event.target.value as ModSource } : r,
                );
                onChange(next);
              }}
            >
              {MOD_SOURCES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              className={synthControlClass}
              value={route.target}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, target: event.target.value as ModTarget } : r,
                );
                onChange(next);
              }}
            >
              {MOD_TARGETS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ParamKnob
              min={-2}
              max={2}
              step={0.01}
              value={route.depth}
              defaultValue={1}
              density="compact"
              size={28}
              valuePlacement="end"
              ariaLabel={`Route ${index + 1} depth`}
              learnPath={instrumentPresetLearnPath(slot, `routes.${index}.depth`)}
              onChange={depth => {
                const next = routes.map((r, i) => (i === index ? { ...r, depth } : r));
                onChange(next);
              }}
            />
            <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
              <Checkbox
                checked={Boolean(route.invert)}
                aria-label={`Route ${index + 1} invert`}
                onCheckedChange={invert => {
                  const next = routes.map((r, i) =>
                    i === index ? { ...r, invert: invert || undefined } : r,
                  );
                  onChange(next);
                }}
              />
              inv
            </label>
            <button
              type="button"
              title="Remove"
              onClick={() => onChange(routes.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const synthRowClass =
  "grid grid-cols-[4rem_1fr_auto] items-center gap-1 text-[11px] text-lab-muted";
const synthControlClass =
  "min-w-0 w-full rounded border border-lab-line bg-lab-panel-deep px-[0.28rem] py-[0.18rem] text-slate-200 [font:inherit]";
const synthValClass =
  "min-w-10 text-right text-[10px] text-slate-300 tabular-nums";

function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== "object") {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

function formatVal(path: string, value: number): string {
  if (path.includes("cutoffHz") || path.includes("glideMs") || path.endsWith("octave") || path.endsWith("semis") || path.includes("detune")) {
    return String(Math.round(value));
  }
  if (Math.abs(value) >= 10 || Number.isInteger(value)) {
    return String(Math.round(value * 100) / 100);
  }
  return value.toFixed(2);
}
